import { type QueryClient } from '@tanstack/query-core'
import {
  createRootRouteWithContext,
  Outlet,
  ScrollRestoration,
  useRouterState
} from '@tanstack/react-router'
import { lazy, Suspense, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'

import { FullScreenSpinner } from '@/components/elements'
import { type AuthContextType } from '@/features/auth/providers'

type RouterContext = {
  auth: AuthContextType
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root
})

const TanStackRouterDevtools =
  import.meta.env.DEV &&
  import.meta.env.VITE_TANSTACK_ROUTER_DEVTOOLS_ENABLED === 'true'
    ? lazy(() =>
        import('@tanstack/router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools
        }))
      )
    : () => null

function Root() {
  const { status, matches } = useRouterState()

  const pageTitle = useMemo(() => {
    return matches
      .map((match) => match.meta?.find((tag) => tag.title)?.title)
      .filter((title): title is NonNullable<typeof title> => title != null)
      .reverse()
      .join(' | ')
  }, [matches])

  return (
    <>
      <Helmet
        defaultTitle="LOGI-GO"
        titleTemplate="%s | LOGI-GO"
        title={pageTitle}
      />
      <Outlet />
      <Suspense>
        <TanStackRouterDevtools />
      </Suspense>
      <ScrollRestoration />
      {status === 'pending' && <FullScreenSpinner />}
    </>
  )
}
